import {
  attachmentTypeOptions,
  EmailAttachment,
} from '@/client/courses/types/courses.type';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';

const StyledAttachmentTrigger = styled.span`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
  font-size: var(--xsmall-font-size);
  color: var(--black-main);
  cursor: pointer;

  .pi {
    font-size: var(--xxsmall-font-size);
  }

  &:hover {
    .pi {
      color: var(--red-main);
    }
  }
`;

type AttachmentTypeMenuProps = {
  attachment?: EmailAttachment | null;
  onChange: (attachment: EmailAttachment | null) => void;
  menuId: string;
  triggerClassName?: string;
  minWidth?: string;
};

export const AttachmentTypeMenu: React.FC<AttachmentTypeMenuProps> = ({
  attachment,
  onChange,
  menuId,
  triggerClassName,
  minWidth = '160px',
}) => {
  const { t } = useTranslation();
  const attachmentMenuRef = useRef<Menu>(null);

  const handleAttachmentTypeChange = useCallback(
    (attachmentType: string) => {
      const newAttachment: EmailAttachment = {
        type: attachmentType,
        id: v4(),
        label: attachment?.label,
      };
      onChange(newAttachment);
    },
    [attachment, onChange, t],
  );

  const attachmentMenuItems: MenuItem[] = useMemo(() => {
    return attachmentTypeOptions(t).map((option) => ({
      label: option.label,
      command: () => handleAttachmentTypeChange(option.value),
    }));
  }, [handleAttachmentTypeChange, t]);

  const toggleAttachmentMenu = (event: React.SyntheticEvent) => {
    attachmentMenuRef.current?.toggle(event);
  };

  return (
    <>
      <Menu
        model={attachmentMenuItems}
        popup
        ref={attachmentMenuRef}
        id={menuId}
        style={{ width: 'auto', minWidth }}
      />
      <StyledAttachmentTrigger
        onClick={toggleAttachmentMenu}
        className={triggerClassName}
        aria-controls={menuId}
      >
        <i className="pi pi-plus" />
        <span>{t('content.email.add.attachment')}</span>
      </StyledAttachmentTrigger>
    </>
  );
};
